import React, { useEffect, useState } from "react";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";

const iconMap = {
  offer: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
  update: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  alert: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

// Dummy notifications data
const dummyNotifications = [
];

function NotificationItem({ notif, onDelete, onArchive }) {
  const [swipeX, setSwipeX] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const onStart = (e) => {
    setStartX(e.type.includes("touch") ? e.touches[0].clientX : e.clientX);
    setStartY(e.type.includes("touch") ? e.touches[0].clientY : e.clientY);
    setIsSwiping(true);
  };

  const onMove = (e) => {
    if (!isSwiping) return;
    const currentX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const currentY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
    const diffX = currentX - startX;
    const diffY = currentY - startY;

    if (Math.abs(diffY) > Math.abs(diffX)) {
      setIsSwiping(false);
      setSwipeX(0);
      return;
    }

    if (Math.abs(diffX) < 150) {
      setSwipeX(diffX);
    }
  };

  const onEnd = () => {
    setIsSwiping(false);
    if (swipeX > 100) {
      onArchive(notif.id);
    } else if (swipeX < -100) {
      onDelete(notif.id);
    } else {
      setSwipeX(0);
    }
  };

  return (
    <div
      className="relative overflow-hidden mb-px"
      style={{ touchAction: "pan-y" }}
      onTouchStart={onStart}
      onTouchMove={onMove}
      onTouchEnd={onEnd}
      onMouseDown={onStart}
      onMouseMove={onMove}
      onMouseUp={onEnd}
      onMouseLeave={onEnd}
    >
      {/* Background Actions */}
      <div className="absolute inset-0 flex items-center justify-end rounded-lg" style={{ background: APP_THEME.errorColor }}>
        {swipeX > 0 && (
          <div className="w-full flex justify-start px-6 text-white text-xs font-bold">
            ARCHIVE
          </div>
        )}
        {swipeX < 0 && (
          <div className="w-full flex justify-end px-6 text-white text-xs font-bold">
            DELETE
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className="flex gap-3.5 p-4 bg-surface border-b relative z-10"
        style={{
          background: APP_THEME.bgColor,
          borderBottomColor: APP_THEME.borderColor,
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? "none" : "transform 0.3s ease",
        }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: notif.type === "alert" ? `${APP_THEME.errorColor}15` : `${APP_THEME.primaryColor}15`,
          }}
        >
          {iconMap[notif.type](notif.type === "alert" ? APP_THEME.errorColor : APP_THEME.primaryColor)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <h4
              className="text-sm m-0"
              style={{
                fontWeight: notif.unread ? 800 : 600,
                color: notif.unread ? APP_THEME.textPrimary : APP_THEME.textSecondary,
                fontFamily: APP_THEME.fontFamily,
              }}
            >
              {notif.title.replace("[APP_NAME]", APP_THEME.appName)}
            </h4>
            <span
              className="text-[11px]"
              style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
            >
              {notif.time}
            </span>
          </div>
          <p
            className="text-[13px] leading-relaxed m-0"
            style={{
              color: APP_THEME.textSecondary,
              fontFamily: APP_THEME.fontFamily,
              opacity: notif.unread ? 1 : 0.7,
            }}
          >
            {notif.message}
          </p>
        </div>
        {notif.unread && (
          <div
            className="absolute top-4 right-3 w-2 h-2 rounded-full"
            style={{
              background: APP_THEME.primaryColor,
              boxShadow: `0 0 8px ${APP_THEME.primaryColor}80`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function Notification() {
  const [notifications, setNotifications] = useState(dummyNotifications);

  const markAllNotificationsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, unread: false }))
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const archiveNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      markAllNotificationsRead();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Header title={"Notifications"} showBack={true} />
      <div className="px-4">
        {notifications.length === 0 ? (
          <div className="text-center py-20">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: APP_THEME.surfaceColor }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <p className="text-base font-semibold mb-1" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
              No new notifications
            </p>
            <p className="text-[13px] mt-1" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
              We'll notify you when something important happens.
            </p>
          </div>
        ) : (
          <div className="pt-2">
            {notifications.map((notif) => (
              <NotificationItem
                key={notif.id}
                notif={notif}
                onDelete={deleteNotification}
                onArchive={archiveNotification}
              />
            ))}
            <p
              className="text-[11px] text-center mt-5 opacity-60"
              style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
            >
              Swipe left to delete or right to archive
            </p>
          </div>
        )}
      </div>

      <style>
        {`
          .duration-300 {
            transition-duration: 300ms;
          }
        `}
      </style>
    </>
  );
}

export default Notification;